import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from '@mui/material';
import RequestInsuranceServiceSummary from './RequestInsuranceServiceSummary';

interface RequestDetailsDialogProps {
  open: boolean;
  request: any | null;
  loading?: boolean;
  onClose: () => void;
}

const RequestDetailsDialog: React.FC<RequestDetailsDialogProps> = ({
  open,
  request,
  loading = false,
  onClose,
}) => {
  const navigate = useNavigate();

  const attrs = request?.attributes || request || {};
  const requestNumber = attrs.requestNumber || (request?.id ? `REQ-${request.id}` : '');

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
          <Typography variant="h6">Request Details</Typography>
          {request && (
            <Chip label={requestNumber} color="primary" size="small" />
          )}
        </Box>
      </DialogTitle>
      <DialogContent>
        {loading || !request ? (
          <Typography color="text.secondary">Loading request details...</Typography>
        ) : (
          <Stack spacing={3} sx={{ mt: 1 }}>
            <RequestInsuranceServiceSummary
              request={request}
              onClientClick={(clientId) => {
                onClose();
                navigate(`/insurance-clients/${clientId}/overview`);
              }}
            />

            <Box>
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                Request Information
              </Typography>
              <Stack spacing={2}>
                <Box>
                  <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase' }}>
                    Status
                  </Typography>
                  <Box sx={{ mt: 0.5 }}>
                    <Chip
                      label={(attrs.status || 'pending').replace(/_/g, ' ')}
                      color={
                        attrs.status === 'completed'
                          ? 'success'
                          : attrs.status === 'cancelled'
                            ? 'error'
                            : attrs.status === 'pending'
                              ? 'warning'
                              : 'info'
                      }
                      size="small"
                    />
                  </Box>
                </Box>
                {attrs.serviceType && (
                  <Box>
                    <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase' }}>
                      Service Type
                    </Typography>
                    <Typography variant="body1" sx={{ mt: 0.5 }}>
                      {attrs.serviceType}
                    </Typography>
                  </Box>
                )}
                {attrs.description && (
                  <Box>
                    <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase' }}>
                      Reason
                    </Typography>
                    <Typography variant="body1" sx={{ mt: 0.5, whiteSpace: 'pre-wrap' }}>
                      {attrs.description}
                    </Typography>
                  </Box>
                )}
                {attrs.locationText && (
                  <Box>
                    <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase' }}>
                      Location
                    </Typography>
                    <Typography variant="body1" sx={{ mt: 0.5 }}>
                      {attrs.locationText}
                    </Typography>
                  </Box>
                )}
              </Stack>
            </Box>
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        {request && (
          <Button
            onClick={() => {
              onClose();
              navigate(`/requests/${request.id || request.attributes?.id}/view`);
            }}
          >
            Open full page
          </Button>
        )}
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default RequestDetailsDialog;
